import { useState, type FormEvent, type ReactNode } from 'react'
import { MASTER_PASSWORD_MIN_LENGTH } from '@kontor/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { KeyRound, Lock, ShieldCheck, Smartphone, TriangleAlert } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { TOTP_REQUIRED_CODE } from '@kontor/shared'

import { ApiRequestError, setupVault, unlockVault } from '@/lib/api'

function GateLayout({
  icon,
  title,
  description,
  children,
}: {
  icon: ReactNode
  title: string
  description: string
  children: ReactNode
}) {
  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {icon}
            {title}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </div>
  )
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) return error.message
  return 'Unerwarteter Fehler. Mehr steht im Server-Log.'
}

/** First start: choose the master password that protects every credential from here on. */
export function VaultSetup() {
  const queryClient = useQueryClient()
  const [password, setPassword] = useState('')
  const [repeat, setRepeat] = useState('')

  const mutation = useMutation({
    mutationFn: () => setupVault(password),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['vault-status'] })
    },
  })

  const tooShort = password.length > 0 && password.length < MASTER_PASSWORD_MIN_LENGTH
  const mismatch = repeat.length > 0 && repeat !== password
  const canSubmit =
    password.length >= MASTER_PASSWORD_MIN_LENGTH && password === repeat && !mutation.isPending

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (canSubmit) mutation.mutate()
  }

  return (
    <GateLayout
      icon={<ShieldCheck className="size-5" aria-hidden="true" />}
      title="Vault einrichten"
      description="Dieses Passwort verschlüsselt alle Server-Zugangsdaten. Es wird nirgends gespeichert."
    >
      <form onSubmit={submit} className="space-y-4">
        <Alert>
          <TriangleAlert />
          <AlertTitle>Es gibt keine Wiederherstellung</AlertTitle>
          <AlertDescription>
            Ohne dieses Passwort sind die hinterlegten Schlüssel und Passwörter verloren. Leg es in
            deinen Passwortmanager, bevor du fortfährst.
          </AlertDescription>
        </Alert>

        <div className="space-y-2">
          <Label htmlFor="master-password">Master-Passwort</Label>
          <Input
            id="master-password"
            type="password"
            autoComplete="new-password"
            autoFocus
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <p className="text-muted-foreground text-xs">
            Mindestens {MASTER_PASSWORD_MIN_LENGTH} Zeichen.
          </p>
          {tooShort ? (
            <p className="text-destructive text-xs">
              Noch {MASTER_PASSWORD_MIN_LENGTH - password.length} Zeichen zu kurz.
            </p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="master-password-repeat">Wiederholen</Label>
          <Input
            id="master-password-repeat"
            type="password"
            autoComplete="new-password"
            value={repeat}
            onChange={(event) => setRepeat(event.target.value)}
          />
          {mismatch ? (
            <p className="text-destructive text-xs">Die beiden Eingaben stimmen nicht überein.</p>
          ) : null}
        </div>

        {mutation.isError ? (
          <Alert variant="destructive">
            <TriangleAlert />
            <AlertTitle>Einrichtung fehlgeschlagen</AlertTitle>
            <AlertDescription>{errorMessage(mutation.error)}</AlertDescription>
          </Alert>
        ) : null}

        <Button type="submit" className="w-full" disabled={!canSubmit}>
          <KeyRound />
          {mutation.isPending ? 'Wird eingerichtet …' : 'Vault einrichten'}
        </Button>
      </form>
    </GateLayout>
  )
}

/** Every later start: the vault exists and needs the master password to become usable. */
export function VaultUnlock() {
  const queryClient = useQueryClient()
  const [password, setPassword] = useState('')
  const [code, setCode] = useState('')

  const mutation = useMutation({
    mutationFn: () => unlockVault(password, code),
    onSuccess: () => {
      setPassword('')
      setCode('')
      void queryClient.invalidateQueries({ queryKey: ['vault-status'] })
    },
  })

  /**
   * The field appears only once the backend has asked for it. Nothing on the unlock screen says
   * beforehand whether this installation has a second factor.
   */
  const needsCode =
    mutation.error instanceof ApiRequestError && mutation.error.code === TOTP_REQUIRED_CODE

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (password.length > 0 && !mutation.isPending) mutation.mutate()
  }

  return (
    <GateLayout
      icon={<Lock className="size-5" aria-hidden="true" />}
      title="Vault entsperren"
      description="Kontor braucht dein Master-Passwort, um die Server-Zugangsdaten zu entschlüsseln."
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="unlock-password">Master-Passwort</Label>
          <Input
            id="unlock-password"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>

        {needsCode ? (
          <div className="space-y-2">
            <Label htmlFor="unlock-totp">Code aus deiner Authenticator-App</Label>
            <Input
              id="unlock-totp"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              placeholder="123456"
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
            <p className="text-muted-foreground text-xs">
              Kein Telefon zur Hand? Ein Wiederherstellungscode geht hier genauso — er gilt dann
              einmal.
            </p>
          </div>
        ) : null}

        {mutation.isError ? (
          <Alert variant={needsCode ? 'default' : 'destructive'}>
            {needsCode ? <Smartphone /> : <TriangleAlert />}
            <AlertTitle>
              {needsCode ? 'Zweiter Faktor nötig' : 'Entsperren fehlgeschlagen'}
            </AlertTitle>
            <AlertDescription>{errorMessage(mutation.error)}</AlertDescription>
          </Alert>
        ) : null}

        <Button
          type="submit"
          className="w-full"
          disabled={password.length === 0 || mutation.isPending}
        >
          <KeyRound />
          {mutation.isPending ? 'Wird entsperrt …' : 'Entsperren'}
        </Button>
      </form>
    </GateLayout>
  )
}
